import React, { useEffect, useMemo, useState } from "react";
import {
	Alert,
	Autocomplete,
	Box,
	Button,
	Checkbox,
	Chip,
	Divider,
	ListItemText,
	MenuItem,
	Paper,
	Stack,
	TextField,
	Typography,
	ToggleButton,
	ToggleButtonGroup,
} from "@mui/material";
import StorefrontIcon from "@mui/icons-material/Storefront";
import BusinessIcon from "@mui/icons-material/Business";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { AuthState } from "context/AuthContext";
import { GlobalState } from "context/GlobalContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { roleAccess } from "../EboPaymentWorking";


const getOptionLabel = (option) =>
	option?.name || option?.label || option?.franchise_name || option?.store_name || option?.storeName || "";

const getOptionKey = (option, index) => option?.id || option?.value || option?.code || option?.key || `${getOptionLabel(option)}-${index}`;

const getStoreKey = (store) =>
	String(store?.store_code ?? store?.code ?? store?.short_code ?? store?.id ?? store?._id ?? getOptionLabel(store)).trim();

const getStoreStatus = (store) => String(store?.store_status || "enabled").toLowerCase();

const SELECT_ALL_STORE_OPTION = {
	id: "__select_all__",
	name: "Select All Stores",
	code: "ALL",
	selectAll: true,
};


const getResponseRows = (data) => {
	if (Array.isArray(data)) return data;
	if (Array.isArray(data?.data)) return data.data;
	if (Array.isArray(data?.result)) return data.result;
	if (Array.isArray(data?.results)) return data.results;
	if (Array.isArray(data?.rows)) return data.rows;
	return [];
};

const normalizeMappedFranchise = (row, index) => ({
	...row,
	id: row?.id ?? row?._id ?? row?.franchise_id ?? `mapped-franchise-${index + 1}`,
	name: row?.name ?? row?.franchise_name ?? row?.franchise ?? row?.label ?? "",
	franchise_name: row?.franchise_name ?? row?.name ?? row?.franchise ?? row?.label ?? "",
	code: row?.code ?? row?.franchise_code ?? row?.franchise_key ?? row?.key ?? "",
	stores: getResponseRows(row?.stores || row?.mapped_stores || row?.franchise_stores || row?.store_list || row?.storeList),
});

const MappingStores = ({
}) => {
	const [mode, setMode] = useState("existing");
	const [cofoStore, setCofoStore] = useState([]);
	const [franchiseList, setFranchiseList] = useState([]);
	const [selectedFranchise, setSelectedFranchise] = useState(null);
	const [newFranchiseName, setNewFranchiseName] = useState("");
	const [selectedStores, setSelectedStores] = useState(() => {
		if (mode === "existing") {
			return franchiseList[0]?.stores || [];
		}
		return [];
	});
	const [isEditingExisting, setIsEditingExisting] = useState(false);
	const [lastSavedPayload, setLastSavedPayload] = useState(null);
	const [error, setError] = useState("");
	const [saving, setSaving] = useState(false);

	const franchiseLabel = mode === "existing" ? getOptionLabel(selectedFranchise) : newFranchiseName.trim();
	const storeCount = selectedStores.length;
	const canEditStores = mode === "new" || isEditingExisting;
	const showSelectAllOption = mode === "new" || (mode === "existing" && isEditingExisting);
	const selectedFranchiseStoreKeys = useMemo(
		() => new Set((selectedFranchise?.stores || []).map((store) => getStoreKey(store)).filter(Boolean)),
		[selectedFranchise]
	);
	const lockedSelectedStores = useMemo(() => {
		if (mode !== "existing" || !isEditingExisting) return [];
		return selectedStores.filter((store) => selectedFranchiseStoreKeys.has(getStoreKey(store)));
	}, [isEditingExisting, mode, selectedFranchiseStoreKeys, selectedStores]);
	const lockedSelectedStoreKeys = useMemo(
		() => new Set(lockedSelectedStores.map((store) => getStoreKey(store)).filter(Boolean)),
		[lockedSelectedStores]
	);

	const otherFranchiseMappedStoreKeys = useMemo(() => {
		const keys = new Set();
		franchiseList.forEach((franchise) => {
			if (mode === "existing" && String(franchise?.id) === String(selectedFranchise?.id)) return;
			(franchise?.stores || []).forEach((store) => {
				const key = getStoreKey(store);
				if (key) keys.add(key);
			});
		});
		return keys;
	}, [franchiseList, mode, selectedFranchise]);
	const selectableStores = useMemo(() => {
		const uniqueStores = new Map();
		const addStore = (store) => {
			const key = getStoreKey(store);
			if (!key) return;
			if (!uniqueStores.has(key)) {
				uniqueStores.set(key, store);
			}
		};

		if (mode === "new") {
			cofoStore.forEach((store) => {
				if (!otherFranchiseMappedStoreKeys.has(getStoreKey(store))) {
					addStore(store);
				}
			});
			return Array.from(uniqueStores.values());
		}
		if (mode === "existing") {
			(selectedFranchise?.stores || []).forEach(addStore);
			cofoStore.forEach((store) => {
				const storeKey = getStoreKey(store);
				if (selectedFranchiseStoreKeys.has(storeKey) || !otherFranchiseMappedStoreKeys.has(storeKey)) {
					addStore(store);
				}
			});
			return Array.from(uniqueStores.values());
		}
		return cofoStore;
	}, [cofoStore, mode, otherFranchiseMappedStoreKeys, selectedFranchise, selectedFranchiseStoreKeys]);
	const storePickerOptions = showSelectAllOption ? [SELECT_ALL_STORE_OPTION, ...selectableStores] : selectableStores;
	const allStoresSelected =
		selectableStores.length > 0 &&
		selectedStores.length === selectableStores.length &&
		selectableStores.every((store) =>
			selectedStores.some((selected) => getStoreKey(selected) === getStoreKey(store))
		);

	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const { user } = AuthState();

	const roleAccessData = roleAccess(user);
	const isReadOnlyUser = ["VIEWER", "EDITOR"].includes(roleAccessData?.roles);

    const handleGetCofoStores = async () => {
            try {
                setLoadingOpen(true);
                await pythonVmInstance({
                    url: `/ebo-payment-working/cofo/cofo-stores`,
                    headers: {
                        "Content-Type": "application/json",
                    },
                    params: {
                        "store_type": "cofo",
                    },
                    method: "GET",
                }).then((res) => {
                    setCofoStore(res?.data);	
                });
                setLoadingOpen(false);
            } catch (err) {
                setSnackBarOpen(true);
                setLoadingOpen(false);
                setSnackBar({
                    ...snackBar,
                    message: "Failed to fetch stores",
                    severity: "error",
                    anchorOrigin: { horizontal: "center", vertical: "top" },
                });
            }
        };

	const handleGetMappedStores = async () => {
		try {
			setLoadingOpen(true);
			const res = await pythonVmInstance({
				url: `/ebo-payment-working/cofo/mapped-stores`,
				headers: {
					"Content-Type": "application/json",
				},
				method: "GET",
			});
			const mappedFranchises = getResponseRows(res?.data).map(normalizeMappedFranchise).filter((row) => row?.franchise_name || row?.name);
			const fallbackFranchises = franchiseList.map(normalizeMappedFranchise);
			const nextFranchiseList = mappedFranchises.length ? mappedFranchises : fallbackFranchises;

			setFranchiseList(nextFranchiseList);
			setSelectedFranchise((currentSelected) => {
				if (!nextFranchiseList.length) return null;
				if (!currentSelected) return nextFranchiseList[0];
				return nextFranchiseList.find((item) => String(item.id) === String(currentSelected.id)) || nextFranchiseList[0];
			});
			setSelectedStores((currentStores) => {
				if (currentStores?.length) return currentStores;
				return nextFranchiseList[0]?.stores || [];
			});
			setLoadingOpen(false);
		} catch (err) {
			const fallbackFranchises = franchiseList.map(normalizeMappedFranchise);
			setFranchiseList(fallbackFranchises);
			setSelectedFranchise(fallbackFranchises[0] || null);
			setSelectedStores(fallbackFranchises[0]?.stores || []);
			setSnackBarOpen(true);
			setLoadingOpen(false);
			setSnackBar({
				...snackBar,
				message: "Failed to fetch mapped franchises",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		}
	};

	const storePreview = useMemo(
		() =>
			selectedStores.map((store, index) => ({
				...store,
				order: index + 1,
			})),
		[selectedStores]
	);

	useEffect(() => {
		handleGetCofoStores();
        handleGetMappedStores();
	}, []);

	useEffect(() => {
		if (!franchiseList.length) return;
		setFranchiseList((prev) => (prev.length ? prev : franchiseList.map(normalizeMappedFranchise)));
		setSelectedFranchise((currentSelected) => {
			if (currentSelected) return currentSelected;
			return normalizeMappedFranchise(franchiseList[0], 0);
		});
	}, [franchiseList]);

	const handleModeChange = (_, nextMode) => {
		if (!nextMode) return;
		setMode(nextMode);
		setError("");
		setLastSavedPayload(null);
		setIsEditingExisting(false);
		if (nextMode === "existing") {
			const fallbackFranchise = franchiseList[0] || null;
			setSelectedFranchise(fallbackFranchise);
			setSelectedStores(fallbackFranchise?.stores || []);
		}
		if (nextMode === "new") {
			setSelectedStores([]);
			setIsEditingExisting(true);
		}
	};

	const handleFranchiseChange = (event) => {
		const next = franchiseList.find((item) => String(item.id) === String(event.target.value)) || null;
		setSelectedFranchise(next);
		setSelectedStores(next?.stores || []);
		setIsEditingExisting(false);
		setError("");
		setLastSavedPayload(null);
	};

	const handleStoreChange = (_, nextValue, reason, details) => {
		const selectedOption = details?.option;
		if (selectedOption?.selectAll) {
			if (allStoresSelected) {
				setSelectedStores(mode === "existing" && isEditingExisting ? lockedSelectedStores : []);
			} else {
				setSelectedStores(selectableStores);
			}
			setError("");
			return;
		}

		const filteredStores = (nextValue || []).filter((option) => !option?.selectAll);
		if (mode === "existing" && isEditingExisting) {
			const mergedStores = [...lockedSelectedStores];
			filteredStores.forEach((store) => {
				const storeKey = getStoreKey(store);
				if (!lockedSelectedStoreKeys.has(storeKey)) {
					mergedStores.push(store);
				}
			});
			setSelectedStores(mergedStores);
		} else {
			setSelectedStores(filteredStores);
		}
		setError("");
	};

	const handleStoreStatusChange = (storeToUpdate, nextStatus) => {
		const targetKey = getStoreKey(storeToUpdate);
		setSelectedStores((currentStores) =>
			currentStores.map((store) =>
				getStoreKey(store) === targetKey
					? {
						...store,
						store_status: nextStatus,
					}
					: store
			)
		);
		setError("");
	};

	const handleSave = async () => {
		const resolvedFranchiseName = mode === "existing" ? getOptionLabel(selectedFranchise) : newFranchiseName.trim();
		if (!resolvedFranchiseName) {
			setError(mode === "existing" ? "Please select an existing franchise." : "Please enter a new franchise name.");
			return;
		}
		// if (selectedStores.length === 0) {
		// 	setError("Please select at least one store.");
		// 	return;
		// }

		const payload = {
			mode,
			franchise:
				mode === "existing"
					? selectedFranchise
					: { name: resolvedFranchiseName },
			stores: selectedStores,
		};

		const requestPayload = {
			store_type: "cofo",
			email: user?.email || "",
			franchise_name: resolvedFranchiseName,
			stores: selectedStores.map((store, index) => ({
				...store,
				store_code: store?.store_code || "",
				admsite_code: store?.admsite_code || "",
				store_name: getOptionLabel(store),
				store_status: store?.store_status || "enabled",
			})),
		};

		try {
			setSaving(true);
			setError("");
			setLoadingOpen(true);
			const response = await pythonVmInstance({
				url: `/ebo-payment-working/cofo/franchise-mapping`,
				headers: {
					"Content-Type": "application/json",
				},
				method: mode === "existing" ? "PUT" : "POST",
				data: requestPayload,
			});
			const savedMapping = response?.data?.data || response?.data?.result || response?.data || {};
			const nextFranchiseRecord = {
				...(mode === "existing" ? selectedFranchise : {}),
				...(savedMapping && typeof savedMapping === "object" ? savedMapping : {}),
				id:
					savedMapping?.id ??
					savedMapping?._id ??
					(mode === "existing" ? selectedFranchise?.id : `${resolvedFranchiseName}-${Date.now()}`),
				name:
					savedMapping?.name ??
					savedMapping?.franchise_name ??
					savedMapping?.franchise ??
					resolvedFranchiseName,
				code:
					savedMapping?.code ??
					savedMapping?.franchise_code ??
					savedMapping?.franchise_key ??
					(mode === "existing" ? selectedFranchise?.code : resolvedFranchiseName),
				stores: selectedStores,
			};

			setFranchiseList((prev) => {
				if (mode === "existing") {
					return prev.map((item) =>
						String(item.id) === String(selectedFranchise?.id) ? nextFranchiseRecord : item
					);
				}
				return [nextFranchiseRecord, ...prev];
			});
			setSelectedFranchise(nextFranchiseRecord);
			setLastSavedPayload(payload);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: mode === "existing" ? "Franchise mapping updated successfully" : "Franchise mapping saved successfully",
				severity: "success",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			if (mode === "existing") {
				setIsEditingExisting(false);
			} else {
				setMode("existing");
				setNewFranchiseName("");
				setIsEditingExisting(false);
			}
		} catch (saveError) {
			setError(saveError?.response?.data?.message || saveError?.message || "Failed to save franchise-store mapping.");
		} finally {
			setSaving(false);
			setLoadingOpen(false);
		}
	};

	const handleReset = () => {
		setMode(mode);
		const fallbackFranchises = franchiseList.length ? franchiseList : franchiseList.map(normalizeMappedFranchise);
		setFranchiseList(fallbackFranchises);
		setSelectedFranchise(fallbackFranchises[0] || null);
		setNewFranchiseName("");
		setSelectedStores(mode === "existing" ? fallbackFranchises[0]?.stores || [] : []);
		setIsEditingExisting(false);
		setLastSavedPayload(null);
		setError("");
	};

	return (
		<Paper
			variant="outlined"
			sx={{
				p: 1.5,
				borderRadius: 2,
				background:
					"linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.96) 100%)",
			}}>
			<Stack spacing={1.5}>
				<Box>
					<Typography variant="subtitle1" fontWeight={800}>
						Map Stores Under a Franchise
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ fontSize: 12.5 }}>
						Use this screen when you only have store data and need to attach those stores to an existing franchise or create a new one first.
					</Typography>
				</Box>

				<ToggleButtonGroup
					value={mode}
					exclusive
					onChange={handleModeChange}
					size="small"
					sx={{ alignSelf: "flex-start", "& .MuiToggleButton-root": { minHeight: 34, py: 0.5 } }}>
					<ToggleButton value="existing" sx={{ px: 1.5, fontSize: 12 }}>
						<Box display="flex" alignItems="center" gap={1}>
							<BusinessIcon sx={{ fontSize: 16 }} />
							Existing Franchise
						</Box>
					</ToggleButton>
					<ToggleButton value="new" sx={{ px: 1.5, fontSize: 12 }} disabled={isReadOnlyUser}>
						<Box display="flex" alignItems="center" gap={1}>
							<AddBusinessIcon sx={{ fontSize: 16 }} />
							Create New Franchise
						</Box>
					</ToggleButton>
				</ToggleButtonGroup>

				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: { xs: "1fr", lg: "1.05fr 0.95fr" },
						gap: 2,
						alignItems: "start",
					}}>
					<Stack spacing={2}>
						<Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
							<Stack spacing={1.25}>
								{mode === "existing" ? (
									<Box
										sx={{
											p: 1.5,
											borderRadius: 2,
											bgcolor: "rgba(59, 130, 246, 0.06)",
											border: "1px solid",
											borderColor: "rgba(59, 130, 246, 0.18)",
										}}>
										<Typography variant="overline" color="primary" fontWeight={800} sx={{ lineHeight: 1.1 }}>
											Existing Franchise Review
										</Typography>
										<Typography variant="subtitle1" fontWeight={800}>
											{franchiseLabel || "Select a franchise"}
										</Typography>
										<Typography variant="body2" color="text.secondary" sx={{ fontSize: 12.5 }}>
											Review the mapped stores first. Use edit mode only when you want to change the store list.
										</Typography>
										<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1.5 }}>
											<Chip label={`${storeCount} store${storeCount === 1 ? "" : "s"} mapped`} size="small" />
											<Chip label={isEditingExisting ? "Edit mode on" : "View mode"} size="small" color={isEditingExisting ? "warning" : "default"} />
										</Box>
									</Box>
								) : (
									<Box
										sx={{
											p: 1.5,
											borderRadius: 2,
											bgcolor: "rgba(16, 185, 129, 0.06)",
											border: "1px solid",
											borderColor: "rgba(16, 185, 129, 0.18)",
										}}>
										<Typography variant="overline" color="success.main" fontWeight={800} sx={{ lineHeight: 1.1 }}>
											Create Franchise Setup
										</Typography>
										<Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.75 }}>
											Add only the franchise name before mapping stores
										</Typography>
										<Stack spacing={1.25}>
											<TextField
												fullWidth
												size="small"
												label="Franchise Name"
												placeholder="Enter franchise name"
												value={newFranchiseName}
												onChange={(event) => {
													setNewFranchiseName(event.target.value);
													setError("");
												}}
												sx={{ "& .MuiInputBase-input": { fontSize: 13 } }}
											/>
										</Stack>
									</Box>
								)}

								{mode === "existing" ? (
									<>
										<TextField
											select
											fullWidth
											size="small"
											label="Existing Franchise"
											value={selectedFranchise?.id || ""}
											onChange={handleFranchiseChange}
											helperText="Choose a franchise that already exists in the system."
											sx={{ "& .MuiInputBase-input": { fontSize: 13 }, "& .MuiFormHelperText-root": { fontSize: 11 } }}
										>
											{franchiseList.length > 0 ? (
												franchiseList.map((franchise, index) => (
													<MenuItem key={getOptionKey(franchise, index)} value={franchise.id}>
														{franchise.franchise_name || franchise.name}
													</MenuItem>
												))
											) : (
												<MenuItem value="" disabled>
													No franchises available
												</MenuItem>
											)}
										</TextField>
										<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
											<Button
												variant="outlined"
												onClick={() => setIsEditingExisting(true)}
												disabled={!selectedFranchise || saving || isEditingExisting || isReadOnlyUser}
												startIcon={<EditOutlinedIcon />}
												size="small"
												sx={{ alignSelf: "flex-start", minHeight: 32, px: 1.5, fontSize: 12 }}>
												Edit
											</Button>
										</Box>
									</>
								) : null}
							</Stack>
						</Paper>

						{ isEditingExisting && <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
							<Stack spacing={1.25}>
								<Typography variant="subtitle2" fontWeight={700}>
									{mode === "existing" ? (isEditingExisting && "Edit Stores") : "Assign Stores"}
								</Typography>
								{canEditStores ? (
									<Autocomplete
										multiple
										options={storePickerOptions}
										value={selectedStores}
										onChange={handleStoreChange}
										disableCloseOnSelect
										limitTags={2}
										getOptionLabel={getOptionLabel}
										isOptionEqualToValue={(option, value) =>
											Boolean(option?.selectAll && value?.selectAll) ||
											String(option?.store_code ?? option?.id ?? option?.code) === String(value?.store_code ?? value?.id ?? value?.code)
										}
										renderOption={(props, option, { selected }) => (
											(() => {
												const isLockedStore = !option?.selectAll && lockedSelectedStoreKeys.has(getStoreKey(option));
												const currentSelectedStore = selectedStores.find(
													(store) => getStoreKey(store) === getStoreKey(option)
												);
												const storeStatus = getStoreStatus(currentSelectedStore || option);
												return (
													<li
														{...props}
														key={getOptionKey(option, props?.id)}
														style={{
															...props.style,
															paddingTop: 4,
															paddingBottom: 4,
															minHeight: 34,
														}}>
														<Checkbox
															sx={{ mr: 0.75, p: 0.35 }}
															checked={option?.selectAll ? allStoresSelected : selected}
															disabled={isLockedStore}
														/>
														<ListItemText
															primary={getOptionLabel(option)}
															secondary={
																option?.selectAll
																	? "Select every store at once"
																	: isLockedStore
																	? `Already mapped to this franchise • ${storeStatus}`
																	: option?.code || option?.store_code || ""
															}
															primaryTypographyProps={{ fontSize: 13, lineHeight: 1.15 }}
															secondaryTypographyProps={{ fontSize: 11, lineHeight: 1.1 }}
															sx={{ my: 0 }}
														/>
														{isLockedStore ? (
															<Button
																size="small"
																variant={storeStatus === "disabled" ? "outlined" : "contained"}
																color={storeStatus === "disabled" ? "inherit" : "warning"}
																onClick={(event) => {
																	event.preventDefault();
																	event.stopPropagation();
																	handleStoreStatusChange(option, "disabled");
																}}
																disabled={storeStatus === "disabled"}
																sx={{ ml: 1, minWidth: 76, fontSize: 11, px: 1 }}>
																{storeStatus === "disabled" ? "Disabled" : "Disable"}
															</Button>
														) : null}
													</li>
												);
											})()
										)}
										renderTags={(value, getTagProps) => {
											const visibleTags = value.slice(0, 2);
											const hiddenCount = value.length - visibleTags.length;
											return (
												<Box sx={{ display: "flex", flexWrap: "nowrap", gap: 0.75, overflow: "hidden" }}>
													{visibleTags.map((option, index) => {
														const tagProps = getTagProps({ index });
														const isLockedStore = lockedSelectedStoreKeys.has(getStoreKey(option));
														return (
															<Chip
																{...tagProps}
																key={getOptionKey(option, index)}
																label={
																	lockedSelectedStoreKeys.has(getStoreKey(option))
																		? `${getOptionLabel(option)} (${getStoreStatus(option)})`
																		: getOptionLabel(option)
																}
																size="small"
																icon={<StorefrontIcon />}
																onDelete={isLockedStore ? undefined : tagProps.onDelete}
																sx={{ maxWidth: 170 }}
																variant={isLockedStore ? "outlined" : "filled"}
															/>
														);
													})}
													{hiddenCount > 0 ? (
														<Chip
															label={`+${hiddenCount} more`}
															size="small"
															variant="outlined"
															sx={{ maxWidth: 110 }}
														/>
													) : null}
												</Box>
											);
										}}
										renderInput={(params) => (
											<TextField
												{...params}
												label="Stores"
												placeholder="Search stores"
												size="small"
												helperText={
													mode === "existing" && isEditingExisting
														? "Existing mapped stores stay selected. You can add new stores from the list."
														: ""
												}
											/>
										)}
										sx={{
											"& .MuiInputBase-root": {
												minHeight: 38,
												alignItems: "flex-start",
												pt: 0.25,
											},
											"& .MuiAutocomplete-input": {
												minWidth: 90,
												fontSize: 13,
											},
										}}
										ListboxProps={{
											style: {
												maxHeight: 280,
												overflow: "auto",
											},
										}}
									/>
								) : (
									""
								)}
							</Stack>
						</Paper>}
					</Stack>

					<Paper
						variant="outlined"
						sx={{
							p: 1.5,
							borderRadius: 2,
							minHeight: 220,
							bgcolor: "rgba(17, 24, 39, 0.03)",
						}}>
						<Stack spacing={1.25}>
							<Box display="flex" alignItems="center" gap={1}>
								<CheckCircleIcon color="success" />
								<Typography variant="subtitle2" fontWeight={700}>
									Mapping Preview
								</Typography>
							</Box>

							<Box>
								<Typography variant="body2" color="text.secondary">
									Franchise Type
								</Typography>
								<Typography variant="body2" fontWeight={600}>
									{mode === "existing" ? "Existing Franchise" : "New Franchise"}
								</Typography>
							</Box>

							<Box>
								<Typography variant="body2" color="text.secondary">
									Franchise Name
								</Typography>
								<Typography variant="body2" fontWeight={600}>
									{franchiseLabel || "Not selected"}
								</Typography>
							</Box>

							{mode === "existing" ? (
								<>
									<Box
										sx={{
											p: 1.25,
											borderRadius: 1.5,
											bgcolor: "background.paper",
											border: "1px solid",
											borderColor: "divider",
										}}>
										<Typography variant="body2" color="text.secondary">
											Current Store Count
										</Typography>
										<Typography variant="h6" fontWeight={800}>
											{storeCount}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											{franchiseLabel || "Selected franchise"} is currently mapped to these stores.
										</Typography>
									</Box>

									<Divider />

									<Box>
										<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
											Store Names
										</Typography>
										<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
											{storePreview.map((store) => (
												<Chip
													key={store.id || store.code || store.name}
													label={getOptionLabel(store)}
													size="small"
													color="primary"
													variant="outlined"
												/>
											))}
										</Box>
									</Box>
								</>
							) : (
								<>
									<Box
										sx={{
											p: 1.25,
											borderRadius: 1.5,
											bgcolor: "background.paper",
											border: "1px solid",
											borderColor: "divider",
										}}>
										<Typography variant="body2" color="text.secondary">
											New Franchise Info
										</Typography>
										<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
											<Chip label={`${storeCount} store${storeCount === 1 ? "" : "s"}`} size="small" color="success" variant="outlined" />
										</Box>
									</Box>

									<Divider />

									<Box>
										<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
											Selected Store Names
										</Typography>
										<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
											{storePreview.length > 0 ? (
												storePreview.map((store) => (
													<Chip
														key={store.id || store.code || store.name}
														label={getOptionLabel(store)}
														size="small"
														color="primary"
														variant="outlined"
													/>
												))
											) : (
												<Typography variant="body2" color="text.secondary">
													Pick stores to see the summary here.
												</Typography>
											)}
										</Box>
									</Box>
								</>
							)}
						</Stack>
					</Paper>
				</Box>

				{error ? <Alert severity="error">{error}</Alert> : null}
				{lastSavedPayload ? (
					<Alert severity="success">
						Mapping ready. {lastSavedPayload.stores.length} store(s) prepared for {franchiseLabel}.
					</Alert>
				) : null}

				<Box display="flex" justifyContent="flex-end" gap={1}>
					{mode === "new" || isEditingExisting ? (
						<Button variant="text" onClick={handleReset} disabled={saving || isReadOnlyUser} size="small" sx={{ minHeight: 32, px: 1.25, fontSize: 12 }}>
							Reset
						</Button>
					) : null}
					{mode === "new" || isEditingExisting ? (
						<Button variant="contained" onClick={handleSave} disabled={saving || isReadOnlyUser} size="small" sx={{ minHeight: 32, px: 1.5, fontSize: 12 }}>
							{saving ? "Saving..." : "Save Mapping"}
						</Button>
					) : null}
				</Box>
			</Stack>
		</Paper>
	);
};

export default MappingStores;
